import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CLINIC } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Stethoscope, FlaskConical, Pill, Syringe, Dumbbell, Truck, Plus, Pencil, Trash2, Phone, MessageCircle, MapPin } from "lucide-react";
import { Toaster, toast } from "sonner";

const CATEGORIES = [
  { key: "doctors", label: "Doctors", icon: Stethoscope, specialtyLabel: "Specialty" },
  { key: "labs", label: "Labs", icon: FlaskConical, specialtyLabel: "Services offered" },
  { key: "pharmacies", label: "Pharmacies", icon: Pill, specialtyLabel: "Notes (delivery? 24x7?)" },
  { key: "phlebotomists", label: "Phlebotomists", icon: Syringe, specialtyLabel: "Coverage area" },
  { key: "trainers", label: "Yoga / Exercise", icon: Dumbbell, specialtyLabel: "Type (yoga / gym / physio)" },
  { key: "couriers", label: "Couriers", icon: Truck, specialtyLabel: "Service type" },
];

export default function Network() {
  const { user } = useAuth();
  const canEdit = user?.role === "doctor" || user?.role === "admin" || user?.role === "owner";
  const [tab, setTab] = useState("doctors");
  const [rows, setRows] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async (cat) => {
    const r = await api.get(`/network?category=${cat}`);
    setRows(r.data.contacts || []);
  };
  useEffect(() => { load(tab); }, [tab]);

  const save = async (draft) => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/network", { ...draft, category: tab });
      toast.success(editing ? "Contact updated" : "Contact added");
      setEditorOpen(false);
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (c) => {
    if (!confirm(`Remove ${c.name} from the directory?`)) return;
    await api.delete(`/network/${c.contact_id}`);
    toast.success("Contact removed");
    load(tab);
  };

  const catMeta = CATEGORIES.find((c) => c.key === tab);

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">REFERRAL ECOSYSTEM</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900">Network directory</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl">
            Your trusted partners — one tap to call or WhatsApp when referring a patient.
            Internal use only; patients never see this page.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="bg-pink-600 hover:bg-pink-700 text-white gap-2"
            data-testid="network-add-btn"
          >
            <Plus size={14} /> Add to {catMeta.label}
          </Button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-slate-200">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
                tab === c.key ? "border-pink-600 text-pink-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`network-tab-${c.key}`}
            >
              <Icon size={14} /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((c) => (
          <div key={c.contact_id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`network-card-${c.contact_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{c.name}</div>
                {c.specialty && <div className="text-xs text-pink-700 mt-0.5">{c.specialty}</div>}
                {c.location && (
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                    <MapPin size={11} /> {c.location}
                  </div>
                )}
                {c.notes && <div className="text-[11px] text-slate-500 mt-1 italic">{c.notes}</div>}
              </div>
              {canEdit && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button onClick={() => { setEditing(c); setEditorOpen(true); }} className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`network-edit-${c.contact_id}`}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => del(c)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`network-del-${c.contact_id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
            {c.mobile && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                <span className="text-xs tabular text-slate-600 flex-1">{c.mobile}</span>
                <a href={`tel:${c.mobile}`} className="p-1.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700" title="Call" data-testid={`network-call-${c.contact_id}`}>
                  <Phone size={13} />
                </a>
                <button
                  onClick={() => openWhatsAppRaw(c.mobile, `Hello ${c.name}, contacting you from ${CLINIC.name} regarding a patient referral.`)}
                  className="p-1.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                  title="WhatsApp"
                  data-testid={`network-wa-${c.contact_id}`}
                >
                  <MessageCircle size={13} />
                </button>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500">
            No {catMeta.label.toLowerCase()} added yet.{canEdit ? ` Click "Add to ${catMeta.label}" to build your network.` : ""}
          </div>
        )}
      </div>

      {editorOpen && (
        <ContactEditor
          key={editing ? editing.contact_id : `new-${tab}`}
          contact={editing}
          catMeta={catMeta}
          onClose={() => setEditorOpen(false)}
          onSave={save}
        />
      )}
    </Layout>
  );
}

function ContactEditor({ contact, catMeta, onClose, onSave }) {
  const [d, setD] = useState(contact
    ? { ...contact }
    : { name: "", specialty: "", mobile: "", location: "", notes: "" });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <catMeta.icon size={15} className="text-pink-600" />
            {contact ? "Edit contact" : `Add to ${catMeta.label}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">NAME *</Label>
            <Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="mt-1" data-testid="network-name-input" />
          </div>
          <div>
            <Label className="overline">{catMeta.specialtyLabel.toUpperCase()}</Label>
            <Input value={d.specialty} onChange={(e) => setD({ ...d, specialty: e.target.value })} className="mt-1" data-testid="network-specialty-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">MOBILE</Label>
              <Input value={d.mobile} onChange={(e) => setD({ ...d, mobile: e.target.value })} placeholder="98xxxxxxxx" className="mt-1 tabular" data-testid="network-mobile-input" />
            </div>
            <div>
              <Label className="overline">LOCATION / AREA</Label>
              <Input value={d.location} onChange={(e) => setD({ ...d, location: e.target.value })} placeholder="e.g. area / locality" className="mt-1" data-testid="network-location-input" />
            </div>
          </div>
          <div>
            <Label className="overline">NOTES</Label>
            <Input value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} placeholder="Optional" className="mt-1" data-testid="network-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(d)} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="network-save-btn">
            {contact ? "Save changes" : "Add contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
